import Addicon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import clsx from "clsx";
import { ChangeEvent, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Button, RadioGrp, Text } from "$components";
import { useDeliveryEDBContext } from "$containers/EDB/ProfileEDB/context/DeliveryContext";
import AddressSelect from "$forms/AddressSelect";
import CField from "$forms/CField";
import PostalCode from "$forms/PostalCode";
import WarehouseSelect from "$forms/WarehouseSelect";
import useAuth from "$hooks/useAuth";
import { CompanyType, DispositionAddressType, DispositionType } from "$types";
import { getDispositionLabels } from "$utils/typeConstants";
import { useStyles } from "../../styles/DeliveryPicker.styles";

const { ON_DOCKYARD, OTHER_PLACE, SUBCONTRACTED_WAREHOUSE } = DispositionType;

export default function DispoType() {
  const classes = useStyles();
  const { isAdmin, isClient, isContributor } = useAuth();

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const {
    canCompleteAddress,
    displayAdressBlock,
    togglePopin,
    onWarehouseChange,
    handleDispositionType,
    setValueAndValidate,
    openWarehousePopin,
    setDeliveryDataDispositionAddress,
    deliveryData,
    setDeliveryData,
    isOnDockyard,
    dockyard,
    warehouses,
    country,
    dispositionType,
    intervenantCompany,
  } = useDeliveryEDBContext();

  const handleFieldChange = (fieldName: string, value: string) => {
    setValue(fieldName, value);

    if (fieldName === "dispositionPlace") {
      setDeliveryData({
        ...deliveryData,
        otherPlace: {
          ...deliveryData.otherPlace,
          [fieldName]: value,
        },
      });
    } else {
      setDeliveryDataDispositionAddress({
        [fieldName]: value,
      });
    }
  };

  // TODO: we should create a function to handle the address change everywhere in the code
  const handleAddressChange = (data: any) => {
    if (!Array.isArray(data)) {
      setValueAndValidate("dispositionAdress", data);
      setDeliveryDataDispositionAddress({
        dispositionAdress: data,
      });
      return;
    }

    let street = "";
    const deliveryAddress: DispositionAddressType = {};

    data.forEach((elem: any) => {
      if (elem.types.includes("postal_code")) {
        setValueAndValidate("dispositionPostalCode", elem.long_name);
        deliveryAddress.dispositionPostalCode = elem.long_name;
      }

      if (elem.types.includes("country")) {
        setValueAndValidate("dispositionCountry", elem.long_name);
        deliveryAddress.dispositionCountry = elem.long_name;
      }

      if (elem.types.includes("locality")) {
        setValueAndValidate("dispositionCity", elem.long_name);
        deliveryAddress.dispositionCity = elem.long_name;
      }

      if (elem.types.includes("street_number")) {
        if (street === "") {
          street += elem.long_name;
        } else {
          street = `${elem.long_name}${street}`;
        }
      }

      if (elem.types.includes("route")) {
        street += ` ${elem.long_name}`;
      }
    });
    setValueAndValidate("dispositionAdress", street);
    deliveryAddress.dispositionAdress = street;
    setDeliveryDataDispositionAddress(deliveryAddress);
  };

  // Corrected code for the getRelevantWarehouses useMemo section
  const getRelevantWarehouses = useMemo(() => {
    if (intervenantCompany) {
      // When an intervenant is selected, only show their warehouses (similar to renderContributorWareHouse)
      return (
        warehouses?.filter((warehouse) => {
          return (
            warehouse?.company?._id === intervenantCompany &&
            (warehouse.companies?.some(
              (c) => c?._id === dockyard?.company?._id
            ) ||
              dockyard?.company?._id === warehouse?.company?._id)
          );
        }) || []
      );
    }

    if (dockyard?.company?._id) {
      // When no intervenant is selected, show warehouses from:
      // 1. Client warehouses: warehouses where parent company is in client list
      // 2. Contributor warehouses: warehouses from companies that are intervenants AND have the client company in their companies list
      // 3. Logistician warehouses: warehouses that belong to logistician companies AND have the client company in their companies list
      const interveningCompanyIds =
        dockyard.interveningCompany?.map((company) => company._id) || [];

      return (
        warehouses?.filter((warehouse) => {
          const isClientWarehouse =
            warehouse.company?._id === dockyard.company?._id;

          const isSubClientWarehouse = warehouse.companies?.some(
            (c) => c?._id === dockyard?.company?._id
          );

          const isContributorWarehouse =
            interveningCompanyIds.includes(warehouse.company?._id) &&
            warehouse.companies?.some((c) => c?._id === dockyard.company?._id);

          const isLogisticianWarehouse =
            warehouse.companies?.some(
              (c) => c?._id === dockyard.company?._id
            ) &&
            warehouse.company?.companyTypes?.includes(CompanyType.LOGISTICIAN);

          return (
            isSubClientWarehouse ||
            isClientWarehouse ||
            isContributorWarehouse ||
            isLogisticianWarehouse
          );
        }) || []
      );
    }

    return [];
  }, [warehouses, intervenantCompany, dockyard]);

  return (
    <>
      <div className={classes.contentStep3Title}>
        <Text className={classes.subtitle}>Lieu de mise à disposition</Text>
      </div>
      <CField
        control={control}
        name="dispositionType"
        error={errors.dispositionType}
        required
      >
        {(props: any) => (
          <RadioGrp
            className={`${classes.contentStep3Content} hide-radio-circles`}
            radios={Object.entries(getDispositionLabels(isContributor())).map(
              ([key, label]) => ({
                label,
                value: key as DispositionType,
              })
            )}
            withDivider
            defaultValue={props.value}
            {...props}
            onChange={(val) => {
              handleDispositionType(val);
            }}
          />
        )}
      </CField>
      <Divider className={classes.divider} />
      {dispositionType === OTHER_PLACE && (
        <div className={classes.contentStep3}>
          <div className={clsx(classes.contentStep3Content, classes.flex)}>
            <div style={{ width: 300 }}>
              <CField
                control={control}
                name="dispositionPlace"
                error={errors.dispositionPlace}
                label="Nom"
                required
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("dispositionPlace", e.target.value);
                }}
              />
            </div>
          </div>
          <Divider className={classes.divider} />
        </div>
      )}
      {dispositionType === SUBCONTRACTED_WAREHOUSE && (
        <div className={classes.contentStep3}>
          <div className={classes.contentStep3Title}>
            <Text className={classes.subtitle}>Entrepôt</Text>
          </div>
          <div className={clsx(classes.contentStep3Content, classes.flex)}>
            <div style={{ marginRight: 4, width: 300 }}>
              <CField
                control={control}
                name="entrepotName"
                error={errors.entrepotName}
              >
                {(props: any) => (
                  <WarehouseSelect
                    valueKey="name"
                    companyId={intervenantCompany || dockyard?.company?._id}
                    intervenantCompanyId={intervenantCompany}
                    onChange={(name, warehouse) => {
                      props.onChange(name);
                      onWarehouseChange(warehouse);
                    }}
                    value={props.value}
                    warehouseOptions={getRelevantWarehouses}
                  />
                )}
              </CField>
            </div>
            {isAdmin() && (
              <Button
                color="secondary"
                variant="contained"
                onClick={() => openWarehousePopin()}
                style={{ padding: 0, minWidth: 40 }}
                disabled={!intervenantCompany && !dockyard?.company?._id}
              >
                <Addicon />
              </Button>
            )}
          </div>
          <Divider className={classes.divider} />
        </div>
      )}
      {/* {dispositionType === COLLECTED && (
        <div className={classes.contentStep3}>
          <div className={classes.contentStep3Title}>
            <Text className={classes.subtitle}>Entrepôt</Text>
          </div>
          <div className={clsx(classes.contentStep3Content, classes.flex)}>
            <div style={{ marginRight: 4, width: 300 }}>
              <CField
                control={control}
                name="entrepotName"
                error={errors.entrepotName}
              >
                {(props: any) => (
                  <WarehouseSelect
                    valueKey="name"
                    companyId={intervenantCompany || dockyard?.company?._id}
                    intervenantCompanyId={intervenantCompany}
                    onChange={(name, warehouse) => {
                      props.onChange(name);
                      onWarehouseChange(warehouse);
                    }}
                    value={props.value}
                    warehouseOptions={getRelevantWarehouses}
                    disabled
                    placeholder={
                      preparationWarehouse?.name || "Entrepôt de préparation"
                    }
                  />
                )}
              </CField>
            </div>
          </div>
          <Divider className={classes.divider} />
        </div>
      )} */}
      {(displayAdressBlock || errors?.dispositionAdress) && (
        <>
          <div className={classes.contentStep3}>
            <div className={classes.contentStep3Title}>
              <Text className={classes.subtitle}>
                Adresse de mise à disposition souhaitée
              </Text>
            </div>

            {errors?.dispositionAdress && dispositionType === ON_DOCKYARD && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Text className={classes.errorText}>
                  {`L'adresse de chantier n'a pas été renseignée OU est
                    incomplète. `}
                  {canCompleteAddress
                    ? "Veuillez la compléter afin de valider votre expression de besoin."
                    : isClient()
                      ? "Pour valider votre Expression de Besoin, veuillez demander à votre Coordinateur Logistique de bien vouloir la compléter."
                      : "Pour valider votre Expression de Besoin, veuillez demander à votre Coordinateur Logistique ou votre client de bien vouloir la compléter."}
                </Text>
                <div style={{ marginBottom: 12, marginTop: 12 }}>
                  <Button
                    onClick={() => {
                      togglePopin(true);
                    }}
                    disabled={!canCompleteAddress}
                  >
                    Compléter l&apos;adresse
                  </Button>
                </div>
              </div>
            )}

            {displayAdressBlock && (
              <div className={clsx(classes.contentStep3Content)}>
                <div className={clsx(classes.row, classes.flex)}>
                  <CField
                    control={control}
                    label="Adresse"
                    disabled={!isAdmin()}
                    name="dispositionAdress"
                    error={errors.dispositionAdress}
                    required={
                      dispositionType !== DispositionType.NEAREST_WAREHOUSE
                    }
                  >
                    {(props) => (
                      <AddressSelect
                        value={props.value}
                        disabled={
                          isOnDockyard ||
                          dispositionType ===
                            DispositionType.SUBCONTRACTED_WAREHOUSE ||
                          dispositionType === DispositionType.COLLECTED
                        }
                        required
                        onChange={handleAddressChange}
                        useOnInputChange={false}
                      />
                    )}
                  </CField>
                </div>
                <div className={clsx(classes.row, classes.flex)}>
                  <CField
                    control={control}
                    name="dispositionAdress2"
                    error={errors.dispositionAdress2}
                    label="Complément d'adresse"
                    disabled={
                      isOnDockyard ||
                      dispositionType ===
                        DispositionType.SUBCONTRACTED_WAREHOUSE ||
                      dispositionType === DispositionType.COLLECTED
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleFieldChange("dispositionAdress2", e.target.value);
                    }}
                  />
                </div>

                <div className={clsx(classes.row, classes.flex)}>
                  <div className={clsx(classes.flex, classes.gap)}>
                    <PostalCode
                      disabled={
                        isOnDockyard ||
                        dispositionType ===
                          DispositionType.SUBCONTRACTED_WAREHOUSE ||
                        dispositionType === DispositionType.COLLECTED
                      }
                      control={control}
                      name="dispositionPostalCode"
                      error={errors.dispositionPostalCode}
                      country={country}
                      // @ts-expect-error this will have to type
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleFieldChange(
                          "dispositionPostalCode",
                          e.target.value
                        );
                      }}
                    />
                    <CField
                      control={control}
                      name="dispositionCity"
                      error={errors.dispositionCity}
                      required
                      label="Ville"
                      disabled={
                        isOnDockyard ||
                        dispositionType ===
                          DispositionType.SUBCONTRACTED_WAREHOUSE ||
                        dispositionType === DispositionType.COLLECTED
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleFieldChange("dispositionCity", e.target.value);
                      }}
                    />
                    <CField
                      control={control}
                      name="dispositionCountry"
                      error={errors.dispositionCountry}
                      required
                      label="Pays"
                      disabled={
                        isOnDockyard ||
                        dispositionType ===
                          DispositionType.SUBCONTRACTED_WAREHOUSE ||
                        dispositionType === DispositionType.COLLECTED
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleFieldChange("dispositionCountry", e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <Divider className={classes.divider} />
        </>
      )}
    </>
  );
}
